import React from 'react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import type { InvitationData } from '@/src/modules/templates/types';
import type { ApiTemplate, ApiInvitation } from '@/src/types';
import { FullScreenPreviewDialog } from './FullScreenPreviewDialog';

export interface DraftPreviewData {
  templateId: string;
  occasionType: string;
  eventName?: string | null;
  eventDate?: string | null;
  eventVenue?: string | null;
  hostName?: string | null;
  customMessage?: string | null;
  rsvpLink?: string | null;
  mediaUrls?: string[];
  customData?: Record<string, unknown> | null;
  /** Optional: full ApiTemplate shape needed to render JSON-schema templates */
  template?: ApiTemplate | null;
}

interface Props {
  open: boolean;
  data: DraftPreviewData;
  onClose: () => void;
  /** Optional second action. Omit to hide it. This modal never publishes. */
  ctaLabel?: string;
  onCta?: () => void;
  closeLabel?: string;
  /** Gold when the second action starts creation. Quiet when publish still needs a confirm. */
  continueTone?: 'commit' | 'quiet';
  badge?: string;
  testId?: string;
}

/**
 * Full-screen modal that renders any InviteCloud template with real draft data.
 * Works for code-rendered templates (registry), JSON-schema templates, and shows
 * a clear placeholder for anything else.
 *
 * Usage:
 *   <DraftPreviewModal open={show} data={draftData} onClose={() => setShow(false)} />
 */
export function DraftPreviewModal({
  open,
  data,
  onClose,
  ctaLabel,
  onCta,
  closeLabel = 'Close Preview',
  continueTone = 'commit',
  badge = 'Draft Preview',
  testId = 'readonly-preview',
}: Props) {
  const invitationData: InvitationData = {
    templateId: data.templateId,
    occasionType: data.occasionType as InvitationData['occasionType'],
    eventName: data.eventName || 'Your Event Name',
    eventDate:
      data.eventDate ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    eventVenue: data.eventVenue || 'The Grand Venue',
    hostName: data.hostName || 'Your Name',
    customMessage: data.customMessage ?? undefined,
    rsvpLink: data.rsvpLink ?? undefined,
    mediaUrls: data.mediaUrls ?? [],
    customData: data.customData ?? undefined,
  };

  // ApiInvitation shape required by JsonTemplateRenderer
  const apiInvitationData: ApiInvitation = {
    id: `draft-preview-${data.templateId}`,
    publicSlug: null,
    templateId: data.templateId,
    occasionType: data.occasionType as ApiInvitation['occasionType'],
    status: 'DRAFT',
    hostedUrl: null,
    eventName: invitationData.eventName,
    eventDate: invitationData.eventDate,
    eventVenue: invitationData.eventVenue,
    hostName: invitationData.hostName,
    customMessage: invitationData.customMessage,
    rsvpLink: invitationData.rsvpLink,
    mediaUrls: invitationData.mediaUrls,
    customData: invitationData.customData ?? undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return (
    <FullScreenPreviewDialog
      open={open}
      onClose={onClose}
      closeLabel={closeLabel}
      continueLabel={ctaLabel}
      onContinue={onCta}
      continueTone={continueTone}
      badge={badge}
      testId={testId}
    >
      {registry.has(data.templateId) ? (
        <TemplateRenderer templateId={data.templateId} data={invitationData} />
      ) : data.template?.renderMode === 'json-renderer' && data.template?.layoutSchema ? (
        <JsonTemplateRenderer
          schema={data.template.layoutSchema}
          data={apiInvitationData}
          theme={data.template.themeConfig ?? undefined}
          mode="preview"
        />
      ) : data.template?.renderMode === 'experience' ? (
        <SceneExperienceRenderer manifest={resolveExperienceManifest(data.templateId, apiInvitationData)} />
      ) : (
        <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4 text-stone-400 px-8 text-center">
          <Icons.Eye size={40} className="opacity-30" />
          <p className="text-lg font-semibold">Preview not available</p>
          <p className="text-sm max-w-sm">
            This design can’t be previewed here yet. You can still go back, or continue with it and fill in your details.
          </p>
          <Button type="button" variant="outline" className="mt-2 text-white border-white/20" onClick={onClose}>
            {closeLabel}
          </Button>
        </div>
      )}
    </FullScreenPreviewDialog>
  );
}
